import React, { useEffect, useState } from 'react';
import Modal from '../../components/Modal';
import { SchemaField, parseFieldEnums } from '../../api/schemaField';
import {
    SchemaFieldKeySelect,
    useSchemaFieldDict,
} from '../../components/SchemaFieldKeySelect';
import { useDictOptions } from '../../utils/dictOptions';
import { confirmDialog } from '../../utils/dialog';
import { useCan } from '../../store';
import SearchSelect from '../../components/SearchSelect';
import { gateFieldsOf } from '../../utils/fieldVisibility';
import { FieldGroupAttrs, Layout, fitsLayout } from '../../utils/fieldGroups';
import { useLayoutEditor } from '../../components/fieldLayout/useLayoutEditor';
import { LayoutFieldList, LayoutTabActions } from '../../components/fieldLayout/LayoutEditorParts';

// 参数集字段集编辑器.
// 字段定义现在挂在参数集本身 (ParamSet.fields), 不再是 arch 级 schema.
// 本组件只管 "拿到一组 FieldSpec 让用户增删改",
// 持久化由父组件 (ParamManage) 通过 updateParamSet({fields: JSON.stringify(newFields)}) 自己写.
//
// 2026-07-31: 字段 key 不再自由输入 — 必须从 Schema 字段字典选择
// (SSOT = xtool-schema, 管理员管理→Schema字段管理 维护; 后端写入时同样校验白名单).

type FieldType = 'num' | 'int' | 'str' | 'bool' | 'enum' | 'array';

export interface FieldSpec extends FieldGroupAttrs {
    k: string;
    label?: string;
    t: FieldType;
    u?: string;
    v?: unknown;
    /** 内联枚举候选 (自定义来源); 与 os 二选一 */
    o?: unknown[];
    /**
     * 枚举来源字典类目 code (如 light_source / cap_action). 与设备 Schema 的 optionsSource 同义:
     * 非空 = 候选值来自字典, 前端按类目拉 code→label. 由 SSOT schema_field.enums_source 决定,
     * 读路径富化注入 (模板/参数行不自造枚举).
     */
    os?: string;
    min?: number;
    max?: number;
    required?: boolean;
}

const BOOL_DEFAULT_OPTS = [
    { value: '0', label: '否' },
    { value: '1', label: '是' },
];

const TYPE_OPTIONS: { value: FieldType; label: string }[] = [
    { value: 'num', label: 'num (数值)' },
    { value: 'int', label: 'int (整数)' },
    { value: 'str', label: 'str (文本)' },
    { value: 'bool', label: 'bool (是/否)' },
    { value: 'enum', label: 'enum (枚举)' },
    { value: 'array', label: 'array (多值, 逗号分隔)' },
];

// 字典 fieldType → FieldSpec.t 映射; 选中字典字段时自动填充 t/u/o/os.
// 显示名只认字段字典 (2026-09-02), 模板不再落 label 快照.
// 枚举来源 (字典 os / 内联 o) 一律继承 SSOT schema_field, 与设备 Schema 同一条规则.
function dictToPatch(d: SchemaField): Partial<FieldSpec> {
    const patch: Partial<FieldSpec> = {
        k: d.fieldKey,
        u: d.unit || undefined,
        label: undefined,
        o: undefined,
        os: undefined,
    };
    const enums = parseFieldEnums(d);
    if (d.enumsSource) {
        patch.t = 'enum';
        patch.os = d.enumsSource;
    } else if (enums.length > 0) {
        patch.t = 'enum';
        patch.o = enums;
    } else if (d.fieldType === 'number') {
        patch.t = 'num';
    } else if (d.fieldType === 'boolean') {
        patch.t = 'bool';
    } else if (d.fieldType === 'array') {
        patch.t = 'array';
    } else if (d.fieldType) {
        patch.t = 'str';
    }
    return patch;
}

export function parseFields(json: string | undefined): FieldSpec[] {
    if (!json) return [];
    try {
        const obj = JSON.parse(json);
        if (Array.isArray(obj)) return obj as FieldSpec[];
    } catch {
        /* ignore */
    }
    return [];
}

// 编辑器里的字段项: 带上它在 fields 数组里的下标 (分组布局下展示顺序 ≠ 数组顺序)
interface FieldItem {
    f: FieldSpec;
    idx: number;
}


interface SchemaEditModalProps {
    open: boolean;
    /** 初始字段集; 关闭后由父组件传新的 */
    initialFields: FieldSpec[];
    /** 初始分组布局 (页签 → 分组); null/不传 = 未启用分组 */
    initialLayout?: Layout;
    /** modal 标题里附带的说明, 例如参数集名 */
    subtitle?: string;
    /** 作用域 (参-04 消歧): template=框架级默认模板 (影响所有新建行), row=仅当前参数行 */
    scope?: 'template' | 'row';
    /** scoped 字典类目 (如 process_type 按工艺框架分列表) 的上下文, 传 archCode */
    dictScope?: string;
    onClose: () => void;
    /** 用户点 "确认" 时回调; 父组件负责持久化 (layout 为 null = 未启用分组) */
    onConfirm: (fields: FieldSpec[], layout: Layout) => Promise<void> | void;
    /** 调用方补充的提示 (渲染在通用说明下方); 如设备向导里说明"确认即写库, 与向导提交无关" */
    hint?: React.ReactNode;
}

const SchemaEditModal = ({
    open,
    initialFields,
    initialLayout = null,
    subtitle,
    scope = 'row',
    dictScope = '',
    onClose,
    onConfirm,
    hint,
}: SchemaEditModalProps) => {
    const canUpdate = useCan('param_row', 'update');
    const [fields, setFields] = useState<FieldSpec[]>([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    // Schema 字段字典 (模块级缓存, 全站一次请求)
    const { dict, byKey: dictByKey, groups: dictGroups } = useSchemaFieldDict();
    const fieldKeys = new Set(fields.map((f) => f.k));
    // 字典来源枚举的候选值 (os → 类目字典), 与设备 Schema 表单同一套取数
    const dictOpts = useDictOptions(
        fields.map((f) => f.os),
        dictScope,
    );

    // 某字段的枚举来源 (SSOT 优先): dict=字典类目 / inline=字段字典的内联枚举 / custom=本地自定义 (存量兼容)
    const enumSourceOf = (f: FieldSpec) => {
        const sf = dictByKey.get(f.k);
        const src = sf?.enumsSource || f.os || '';
        if (src)
            return { kind: 'dict' as const, src, opts: dictOpts[src] || [] };
        const ssotEnums = sf ? parseFieldEnums(sf) : [];
        if (ssotEnums.length > 0) {
            return {
                kind: 'inline' as const,
                src: '',
                opts: ssotEnums.map((o) => ({
                    code: String(o),
                    label: String(o),
                })),
            };
        }
        return {
            kind: 'custom' as const,
            src: '',
            opts: (f.o || []).map((o) => ({
                code: String(o),
                label: String(o),
            })),
        };
    };

    useEffect(() => {
        if (!open) return;
        setError('');
        // 显示名只认字段字典 (2026-09-02 拍板): 历史快照里的 label 载入即剥离,
        // 模板再保存时顺手洗掉; 改名统一走「Schema 字段管理」.
        setFields(
            initialFields.map((f) => ({
                ...f,
                label: undefined,
                o: f.o ? [...f.o] : undefined,
            })),
        );
        ed.setLayout(initialLayout ? initialLayout.map((t) => ({ ...t, sections: [...t.sections] })) : null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialFields, initialLayout]);

    // 页签 → 分组 编辑 (与设备字段组编辑器共用, 见 components/layout/useLayoutEditor)
    const ed = useLayoutEditor<FieldSpec>({
        fields,
        setFields,
        makeField: (tab, sec) => (tab ? { k: '', t: 'num', tab, sec } : { k: '', t: 'num' }),
        setError,
    });
    const { layout, grouped } = ed;
    // 删字段要二次确认: 删除按钮紧贴上下移按钮, 误触会静默丢掉一个字段 (保存后才发现)
    const onDelField = async (i: number) => {
        const f = fields[i];
        const name =
            (f && (dictByKey.get(f.k)?.fieldName || f.k)) || `第 ${i + 1} 行`;
        if (
            !(await confirmDialog(
                `确认删除字段「${name}」? 保存后该字段从本模板/本行移除.`,
            ))
        )
            return;
        setFields(fields.filter((_, idx) => idx !== i));
    };
    // 上下移: 分组布局下只和同组的相邻字段换位
    const neighborOf = ed.neighborOf;
    const onMove = (i: number, dir: -1 | 1) => {
        const j = neighborOf(i, dir);
        if (j < 0) return;
        const next = [...fields];
        [next[i], next[j]] = [next[j], next[i]];
        setFields(next);
    };
    const onPatch = (i: number, patch: Partial<FieldSpec>) => {
        const next = [...fields];
        next[i] = { ...next[i], ...patch };
        if (patch.t && patch.t !== fields[i].t) {
            // 切类型时清掉旧类型残留的辅助字段
            delete next[i].o;
            delete next[i].min;
            delete next[i].max;
            next[i].v = undefined;
        }
        setFields(next);
    };

    // 枚举选项: 当前所有 chip + 末尾一个 input 框, 回车 / 失焦 / 点 + 加进列表
    const [enumDrafts, setEnumDrafts] = useState<Record<number, string>>({});
    const commitEnumDraft = (i: number) => {
        const v = (enumDrafts[i] || '').trim();
        if (!v) return;
        const cur = (fields[i].o || []).map(String);
        if (cur.includes(v)) {
            setEnumDrafts((d) => ({ ...d, [i]: '' }));
            return;
        }
        onPatch(i, { o: [...cur, v] });
        setEnumDrafts((d) => ({ ...d, [i]: '' }));
    };
    const removeEnumOpt = async (i: number, opt: string) => {
        if (!(await confirmDialog(`确认删除枚举选项「${opt}」?`))) return;
        const cur = (fields[i].o || []).map(String).filter((x) => x !== opt);
        const patch: Partial<FieldSpec> = { o: cur };
        if (fields[i].v === opt) patch.v = undefined;
        onPatch(i, patch);
    };

    const validate = (): string => {
        if (fields.length === 0) return '至少需要 1 个字段';
        const outside = fields.filter((f) => !fitsLayout(f, layout));
        if (outside.length > 0)
            return `${outside.length} 个字段不在任何分组里 (如 ${outside[0].k || '未选字段'}), 请删除或在分组里重新添加`;
        const seen = new Set<string>();
        for (let i = 0; i < fields.length; i++) {
            const f = fields[i];
            if (!f.k.trim()) return `第 ${i + 1} 行: k 不能为空`;
            if (dict.length > 0 && !dictByKey.has(f.k))
                return `第 ${i + 1} 行: k="${f.k}" 不在 Schema 字段字典中 (管理员管理→Schema字段管理)`;
            if (seen.has(f.k)) return `第 ${i + 1} 行: k="${f.k}" 重复`;
            seen.add(f.k);
            // enum 候选可来自字典 (os) / 字段字典内联 / 本地自定义 (o); 三者全空才算没配
            if (
                f.t === 'enum' &&
                !f.os &&
                !f.o?.length &&
                enumSourceOf(f).opts.length === 0
            )
                return `第 ${i + 1} 行: enum 至少需 1 个候选值 (自定义添加, 或去 Schema字段管理 给该字段配枚举/字典来源)`;
        }
        return '';
    };

    const onSave = async () => {
        const msg = validate();
        if (msg) {
            setError(msg);
            return;
        }
        setError('');
        setBusy(true);
        try {
            await onConfirm(fields, grouped ? layout : null);
            onClose();
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setBusy(false);
        }
    };

    // 单个字段卡片 (平铺 / 分组两种布局共用)
    const renderCard = ({ f, idx }: FieldItem) => (
        <div className="card p-3 bg-slate-50/60">
            <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-slate-500">
                    字段 {idx + 1}
                </span>
                <div className="flex gap-1">
                    <button
                        type="button"
                        className="w-6 h-6 rounded border border-slate-200 text-slate-400 hover:bg-slate-100 disabled:opacity-30"
                        disabled={neighborOf(idx, -1) < 0}
                        onClick={() => onMove(idx, -1)}
                        hidden={!canUpdate}
                    >
                        ↑
                    </button>
                    <button
                        type="button"
                        className="w-6 h-6 rounded border border-slate-200 text-slate-400 hover:bg-slate-100 disabled:opacity-30"
                        disabled={neighborOf(idx, 1) < 0}
                        onClick={() => onMove(idx, 1)}
                        hidden={!canUpdate}
                    >
                        ↓
                    </button>
                    <button
                        type="button"
                        className="w-6 h-6 rounded border border-slate-200 text-slate-400 hover:bg-rose-50 hover:text-rose-500"
                        onClick={() => onDelField(idx)}
                    >
                        −
                    </button>
                </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
                <div>
                    <label className="field-label">
                        k{' '}
                        <span className="text-rose-500">*</span>
                        <span className="text-slate-400 font-normal">
                            {' '}
                            (来自字段字典)
                        </span>
                    </label>
                    <SchemaFieldKeySelect
                        value={f.k}
                        byKey={dictByKey}
                        groups={dictGroups}
                        valueKeyOnly
                        onSelect={(d, key) =>
                            onPatch(
                                idx,
                                d ? dictToPatch(d) : { k: key },
                            )
                        }
                    />
                </div>
                <div>
                    <label className="field-label">
                        显示名
                    </label>
                    <div className="inp bg-slate-50 text-slate-600 flex items-center justify-center">
                        {dictByKey.get(f.k)?.fieldName ||
                            f.k ||
                            '—'}
                    </div>
                    {(() => {
                        const gates = gateFieldsOf(f.k).filter(
                            (g) => fieldKeys.has(g),
                        );
                        return gates.length > 0 ? (
                            <div
                                className="text-[11px] text-slate-400 mt-1"
                                title="参数页上, 这些字段开启 / 取值匹配时才显示本字段"
                            >
                                ↳ 联动:{' '}
                                {gates
                                    .map(
                                        (g) =>
                                            dictByKey.get(g)
                                                ?.fieldName ||
                                            g,
                                    )
                                    .join('、')}
                            </div>
                        ) : null;
                    })()}
                </div>
                <div>
                    <label className="field-label">
                        t{' '}
                        <span className="text-rose-500">*</span>
                    </label>
                    <SearchSelect
                        className="w-full"
                        value={f.t}
                        onChange={(v) =>
                            onPatch(idx, {
                                t: v as FieldType,
                            })
                        }
                        options={TYPE_OPTIONS}
                    />
                </div>
                {(f.t === 'num' || f.t === 'int') && (
                    <>
                        <div>
                            <label className="field-label">
                                单位 u
                            </label>
                            <input
                                className="inp"
                                placeholder="%"
                                value={f.u || ''}
                                onChange={(e) =>
                                    onPatch(idx, {
                                        u: e.target.value,
                                    })
                                }
                            />
                        </div>
                        <div>
                            <label className="field-label">
                                min
                            </label>
                            <input
                                className="inp"
                                type="number"
                                value={f.min ?? ''}
                                onChange={(e) =>
                                    onPatch(idx, {
                                        min:
                                            e.target.value ===
                                            ''
                                                ? undefined
                                                : Number(
                                                      e.target
                                                          .value,
                                                  ),
                                    })
                                }
                            />
                        </div>
                        <div>
                            <label className="field-label">
                                max
                            </label>
                            <input
                                className="inp"
                                type="number"
                                value={f.max ?? ''}
                                onChange={(e) =>
                                    onPatch(idx, {
                                        max:
                                            e.target.value ===
                                            ''
                                                ? undefined
                                                : Number(
                                                      e.target
                                                          .value,
                                                  ),
                                    })
                                }
                            />
                        </div>
                        <div className="col-span-3">
                            <label className="field-label">
                                默认值 v
                            </label>
                            <input
                                className="inp"
                                type="number"
                                value={
                                    (f.v as
                                        | number
                                        | undefined) ?? ''
                                }
                                onChange={(e) =>
                                    onPatch(idx, {
                                        v:
                                            e.target.value ===
                                            ''
                                                ? undefined
                                                : Number(
                                                      e.target
                                                          .value,
                                                  ),
                                    })
                                }
                            />
                        </div>
                    </>
                )}
                {f.t === 'enum' &&
                    (() => {
                        const es = enumSourceOf(f);
                        return (
                            <>
                                <div className="col-span-3">
                                    <label className="field-label">
                                        候选值来源
                                        {es.kind === 'dict' && (
                                            <span className="text-slate-400 font-normal">
                                                {' '}
                                                — 字典类目{' '}
                                                <b className="text-slate-600">
                                                    {es.src}
                                                </b>{' '}
                                                (字典维护页增删即时生效)
                                            </span>
                                        )}
                                        {es.kind ===
                                            'inline' && (
                                            <span className="text-slate-400 font-normal">
                                                {' '}
                                                —
                                                字段字典内联枚举
                                                (Schema字段管理
                                                维护)
                                            </span>
                                        )}
                                        {es.kind ===
                                            'custom' && (
                                            <>
                                                {' '}
                                                <span className="text-rose-500">
                                                    *
                                                </span>
                                                <span className="text-slate-400 font-normal">
                                                    {' '}
                                                    — 自定义:
                                                    回车或点 +
                                                    添加, 每个
                                                    chip 上 ×
                                                    删除
                                                </span>
                                            </>
                                        )}
                                    </label>
                                    {es.kind === 'custom' ? (
                                        <div className="border border-slate-200 rounded bg-white px-2 py-1.5 flex flex-wrap gap-1.5 items-center min-h-[36px]">
                                            {(f.o || []).map(
                                                (opt) => (
                                                    <span
                                                        key={String(
                                                            opt,
                                                        )}
                                                        className="tag bg-brand-50 text-brand-700 flex items-center gap-1"
                                                    >
                                                        {String(
                                                            opt,
                                                        )}
                                                        <button
                                                            type="button"
                                                            className="text-brand-400 hover:text-rose-500 ml-0.5"
                                                            onClick={() =>
                                                                removeEnumOpt(
                                                                    idx,
                                                                    String(
                                                                        opt,
                                                                    ),
                                                                )
                                                            }
                                                            title={`删除选项 ${String(opt)}`}
                                                        >
                                                            ×
                                                        </button>
                                                    </span>
                                                ),
                                            )}
                                            <input
                                                className="flex-1 min-w-[100px] outline-none text-sm bg-transparent"
                                                placeholder="输入新选项, 回车添加"
                                                value={
                                                    enumDrafts[
                                                        idx
                                                    ] || ''
                                                }
                                                onChange={(e) =>
                                                    setEnumDrafts(
                                                        (
                                                            d,
                                                        ) => ({
                                                            ...d,
                                                            [idx]: e
                                                                .target
                                                                .value,
                                                        }),
                                                    )
                                                }
                                                onKeyDown={(
                                                    e,
                                                ) => {
                                                    if (
                                                        e.key ===
                                                        'Enter'
                                                    ) {
                                                        e.preventDefault();
                                                        commitEnumDraft(
                                                            idx,
                                                        );
                                                    }
                                                }}
                                                onBlur={() =>
                                                    commitEnumDraft(
                                                        idx,
                                                    )
                                                }
                                            />
                                            <button
                                                type="button"
                                                className="text-xs px-2 py-0.5 rounded border border-slate-200 hover:bg-slate-50"
                                                onClick={() =>
                                                    commitEnumDraft(
                                                        idx,
                                                    )
                                                }
                                                disabled={
                                                    !(
                                                        enumDrafts[
                                                            idx
                                                        ] || ''
                                                    ).trim()
                                                }
                                                hidden={
                                                    !canUpdate
                                                }
                                            >
                                                + 添加
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="border border-slate-200 rounded bg-slate-50 px-2 py-1.5 flex flex-wrap gap-1.5 items-center min-h-[36px]">
                                            {es.opts.length ===
                                            0 ? (
                                                <span className="text-xs text-slate-400">
                                                    {es.kind ===
                                                    'dict'
                                                        ? `字典 ${es.src} 暂无启用项`
                                                        : '暂无候选值'}
                                                </span>
                                            ) : (
                                                es.opts.map(
                                                    (o) => (
                                                        <span
                                                            key={
                                                                o.code
                                                            }
                                                            className="tag bg-slate-100 text-slate-600"
                                                            title={
                                                                o.code
                                                            }
                                                        >
                                                            {
                                                                o.label
                                                            }
                                                        </span>
                                                    ),
                                                )
                                            )}
                                        </div>
                                    )}
                                    {es.kind !== 'custom' && (
                                        <div className="text-[11px] text-slate-400 mt-1">
                                            候选值继承字段字典
                                            (SSOT), 模板不可改 —
                                            需调整去 管理员管理
                                            →
                                            {es.kind === 'dict'
                                                ? ' 字典维护'
                                                : ' Schema字段管理'}
                                        </div>
                                    )}
                                </div>
                                <div className="col-span-3">
                                    <label className="field-label">
                                        默认值 v
                                    </label>
                                    <SearchSelect
                                        className="w-full"
                                        value={
                                            (f.v as
                                                | string
                                                | undefined) ??
                                            ''
                                        }
                                        onChange={(v) =>
                                            onPatch(idx, { v })
                                        }
                                        placeholder="--"
                                        options={es.opts.map(
                                            (o) => ({
                                                value: o.code,
                                                label: o.label,
                                            }),
                                        )}
                                        clearable
                                    />
                                </div>
                            </>
                        );
                    })()}
                {f.t === 'bool' && (
                    <div className="col-span-3">
                        <label className="field-label">
                            默认值 v
                        </label>
                        <SearchSelect
                            className="w-full"
                            value={f.v ? '1' : '0'}
                            onChange={(v) =>
                                onPatch(idx, {
                                    v: v === '1',
                                })
                            }
                            options={BOOL_DEFAULT_OPTS}
                        />
                    </div>
                )}
                {f.t === 'str' && (
                    <div className="col-span-3">
                        <label className="field-label">
                            默认值 v
                        </label>
                        <input
                            className="inp"
                            value={
                                (f.v as string | undefined) ??
                                ''
                            }
                            onChange={(e) =>
                                onPatch(idx, {
                                    v: e.target.value,
                                })
                            }
                        />
                    </div>
                )}
                <div className="col-span-3">
                    <label className="inline-flex items-center gap-2 text-xs text-slate-600">
                        <input
                            type="checkbox"
                            checked={!!f.required}
                            onChange={(e) =>
                                onPatch(idx, {
                                    required: e.target.checked,
                                })
                            }
                        />
                        必填
                    </label>
                </div>
            </div>
        </div>
    );

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={`管理参数行字段${subtitle ? ` · ${subtitle}` : ''}`}
            width={760}
        >
            <div className="space-y-3">
                {/* 作用域提示放最上面: 先知道改的是框架模板还是单行 */}
                <div>
                <span
                    className={`tag ${
                        scope === 'template'
                            ? 'bg-amber-50 text-amber-700 border border-amber-200'
                            : 'bg-slate-100 text-slate-500'
                    }`}
                >
                    {scope === 'template'
                        ? '⚠ 框架级模板 — 影响本框架所有「新建」参数行的默认字段集'
                        : '仅当前参数行 — 不影响其他行与框架模板'}
                </span>
                </div>
                <div className="text-xs text-slate-500">
                    字段定义跟参数集绑定. 加/减/改字段后点 <b>确认</b> 立即生效,
                    参数行表单同步刷新.
                </div>
                {hint && <div className="warnbox text-xs">{hint}</div>}

                {error && (
                    <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                        {error}
                    </div>
                )}

                <div className="flex items-center justify-between gap-3">
                    <div className="text-sm font-semibold">
                        字段 ({fields.length})
                    </div>
                    {/* 页签操作作用于下方当前选中的页签 */}
                    <LayoutTabActions ed={ed} canUpdate={canUpdate} />
                    <button
                        type="button"
                        className="btn-ghost"
                        onClick={() => ed.addField()}
                        // 分组模板里字段跟着分组走: 只从分组标题旁的「+ 添加字段」加
                        hidden={!canUpdate || grouped}
                    >
                        + 添加字段
                    </button>
                </div>

                {fields.length === 0 && (
                    <div className="border-2 border-dashed border-slate-200 rounded p-6 text-center text-sm text-slate-400">
                        暂无字段, 点 + 添加字段 开始
                    </div>
                )}

                <div className="space-y-2 max-h-[520px] overflow-auto pr-1">
                    <LayoutFieldList
                        ed={ed}
                        canUpdate={canUpdate}
                        renderItem={renderCard}
                        hint="建页签后, 本编辑器与参数页都按 页签 → 分组 嵌套展示"
                    />
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                    <button
                        type="button"
                        className="btn-ghost"
                        onClick={onClose}
                        disabled={busy}
                    >
                        取消
                    </button>
                    <button
                        type="button"
                        className="btn-primary disabled:opacity-50"
                        onClick={onSave}
                        disabled={busy || fields.length === 0}
                    >
                        {busy ? '保存中…' : '确认'}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default SchemaEditModal;
